import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { EmployeeBulkActionModal } from '@/components/bulk-action-modal';
import EmployeeNotificationModal from '@/components/email-templates/EmployeeNotificationModal';
import {
  noticeActionOptions,
  noticeTemplateIdOf,
} from '@/components/email-templates/noticeActions';
import { TableActionDropdown } from '@/components/data-table-actions';
import EmployeeStatus from '@/components/employee/EmployeeStatus';
import { HeaderSkeleton, ProfileGuideButton } from '@/components/profile';
import {
  OPERATION,
  PERMISSIONS,
  ROLES,
  ROLES_PERMISSIONS,
  ROUTES,
} from '@/constants';
import { useEnforceAccessControl } from '@/hooks';
import PrivateAction from '@/routes/PrivateAction';
import { QUERY_KEY } from '@/shared/constants';
import { EMPLOYEES_BULK_ACTION_OPTIONS } from '@/shared/employee';
import { useAuthStore } from '@/store/useAuthStore';
import { DATE_FORMATS } from '@/utils/formUtils';
import {
  getActionLinkUrl,
  getUserFullName,
  groupArrayByKey,
} from '@/utils/utils';
import EmployeeRestoreModal from '../../_components/actions/EmployeeRestoreModal';
import ExtendEmploymentModal from '../../_components/actions/ExtendEmploymentModal';
import TimeSheetButton from '../../_components/actions/TimeSheetButton';
import TeamBackButton from '@/components/team-assignments/TeamBackButton';
import EmployeeProfilePhoto from './EmployeeProfilePhoto';

const ZAZ_SEND_ACTION = 'zaz-send';

// The ZAZ dialog lives with the profile (useEmployeeZaz); picking the action
// only opens it and clears the dropdown's pick.
function ZazActionOpener({ onOpen, onDone }) {
  useEffect(() => {
    onOpen?.();
    onDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/**
 * Teal header of the employee profile: identity on the left, shortcuts and
 * actions on the right.
 */
export default function EmployeeProfileHeader({
  employee,
  employeeId,
  isLoading,
  hasError,
  backUrl,
  access,
  hasHrFlows,
  showTimesheet,
  canExtendFromHere,
  setQueryParams,
  onOpenDocuments,
  onOpenRequest,
  onOpenCompany,
  onOpenOz,
  onOpenGuide,
  onboarding = null,
  zaz = null,
}) {
  const { t } = useTranslation([
    'menus',
    'forms',
    'tables',
    'translations',
    'employeeNotices',
    'zaz',
  ]);
  const tn = (key, opts) => t(key, { ns: 'employeeNotices', ...opts });
  const tp = (key, opts) =>
    t(`employeeProfile.${key}`, { ns: 'translations', ...opts });

  const user = useAuthStore((state) => state.user);
  const company = employee?.company;
  const fullName = getUserFullName(employee?.firstName, employee?.lastName);
  const endDate = employee?.employmentSettings?.endDate;
  const linkedUserId =
    typeof employee?.userId === 'string'
      ? employee.userId
      : employee?.userId?._id;
  const userEditUrl = linkedUserId
    ? getActionLinkUrl({
        route: `${ROUTES.admin}/${ROUTES.users}/${OPERATION.edit}`,
        resource: linkedUserId,
      })
    : null;

  // Invitations: same gate as the invitation modal they open.
  const canInviteRole = useEnforceAccessControl({
    requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.hrOfficer],
    requiredPermissions: [PERMISSIONS.employeeEdit],
  });
  const canInvite = !!access?.canManageEmployment && canInviteRole;
  // Notices: the company's email templates, for whoever the server lets send.
  const noticeTemplates = onboarding?.canNotify
    ? onboarding.notice?.templates
    : null;

  const bulkActionOptions = useMemo(
    () =>
      groupArrayByKey(
        [
          ...(canInvite
            ? EMPLOYEES_BULK_ACTION_OPTIONS.map((option) => ({
                ...option,
                label: t(option.key, { ns: 'forms' }),
                groupValue: t(option.groupKey, { ns: 'forms' }),
              }))
            : []),
          ...noticeActionOptions(noticeTemplates, tn('actionsGroup')),
          // ZAZ to the head of the sector (HR, while none is open).
          ...(zaz?.canSend
            ? [
                {
                  key: ZAZ_SEND_ACTION,
                  value: ZAZ_SEND_ACTION,
                  label: t('sendAction', { ns: 'zaz' }),
                  groupValue: tn('actionsGroup'),
                  dotColor: 'bg-secondary',
                },
              ]
            : []),
        ],
        ({ groupValue }) => groupValue
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, canInvite, noticeTemplates, zaz?.canSend]
  );
  const hasActions = Object.keys(bulkActionOptions).length > 0;

  return (
    <header className="flex flex-wrap items-center justify-between gap-3 bg-accent px-4 pb-1 pt-2.5 text-white xl:flex-nowrap">
      {/* On one row with the actions from xl up: the name and the line under
          it give way (cut short) so the buttons never drop below. */}
      <div
        data-tour="profile-header"
        className="flex min-w-0 items-center gap-3 xl:flex-1"
      >
        <Link
          to={backUrl}
          aria-label={t('back', { ns: 'menus' })}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 hover:text-white"
        >
          <ArrowLeftOutlined />
        </Link>
        {hasError ? null : isLoading ? (
          <HeaderSkeleton />
        ) : (
          <>
            <EmployeeProfilePhoto
              employee={employee}
              employeeId={employeeId}
              canEdit={!!access?.canEditPhoto}
            />
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className="truncate text-xl font-extrabold"
                  title={fullName}
                >
                  {fullName}
                </span>
                <span className="shrink-0">
                  <EmployeeStatus status={employee?.status} />
                </span>
                {canExtendFromHere && endDate && (
                  <span className="shrink-0 whitespace-nowrap rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700">
                    {tp('contractExpired', {
                      date: dayjs(endDate).format(DATE_FORMATS.DISPLAY),
                    })}
                  </span>
                )}
              </div>
              <div className="flex min-w-0 items-center gap-1.5 text-[13px] opacity-95">
                {employee?.generalData?.position && (
                  <span
                    className="min-w-0 truncate"
                    title={employee.generalData.position}
                  >
                    {employee.generalData.position} ·
                  </span>
                )}
                {company?.name && (
                  <button
                    type="button"
                    onClick={onOpenCompany}
                    title={company.name}
                    className="max-w-[60%] shrink-0 cursor-pointer truncate whitespace-nowrap border-0 border-b-[1.5px] border-dashed border-white/70 bg-transparent p-0 text-[13px] font-bold text-white"
                  >
                    {company.name}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {employee && (
        <div
          data-tour="profile-actions"
          className="employee-profile__actions ml-auto flex flex-wrap items-center justify-end gap-2 xl:shrink-0 xl:flex-nowrap">
          {employee.status === 'SUSPENDED' &&
            String(user?.employee) !== String(employeeId) && (
              <EmployeeRestoreModal record={employee} />
            )}
          {/* HLB team board (team assignments), for HLB staff only. */}
          <TeamBackButton employeeId={employeeId} variant="profile" />
          {access.isTechAdmin && userEditUrl && (
            <Link to={userEditUrl} className="employee-profile__hb">
              {tp('user')}
            </Link>
          )}
          {hasHrFlows && access.canSeeRequests && (
            <button
              type="button"
              className="employee-profile__hb"
              onClick={onOpenDocuments}
            >
              {t('hrDocuments', { ns: 'menus' })}
            </button>
          )}
          {hasHrFlows && access.canCreateRequest && (
            <button
              type="button"
              className="employee-profile__hb"
              onClick={onOpenRequest}
            >
              {tp('newRequest')}
            </button>
          )}
          {onboarding?.canRequestEquipment && (
            <button
              type="button"
              className="employee-profile__hb"
              onClick={onboarding.openEquipment}
            >
              {tn('requestEquipment')}
            </button>
          )}
          {access.canSeeOz && (
            <button
              type="button"
              className="employee-profile__hb !border-[#FFCC00]"
              onClick={onOpenOz}
            >
              {tp('ozProfile')}
            </button>
          )}
          {showTimesheet && (
            <TimeSheetButton employeeId={employeeId} company={company} />
          )}
          {canExtendFromHere && access.canManageEmployment && (
            <PrivateAction {...ROLES_PERMISSIONS.EMPLOYEES.CREATE}>
              <ExtendEmploymentModal
                preset={{ employeeId, companyId: company?._id }}
              />
            </PrivateAction>
          )}
          {/* Invitations and the company's notices (email templates). */}
          {hasActions && (
            <TableActionDropdown
              options={bulkActionOptions}
              label={t('actions', { ns: 'tables' })}
            >
              {({ selected: action, onRemove, onClose }) =>
                action === ZAZ_SEND_ACTION ? (
                  <ZazActionOpener
                    onOpen={zaz?.openSend}
                    onDone={() => {
                      onClose();
                      onRemove();
                    }}
                  />
                ) : noticeTemplateIdOf(action) ? (
                  <EmployeeNotificationModal
                    open
                    onClose={() => {
                      onClose();
                      onRemove();
                    }}
                    employeeId={employeeId}
                    state={onboarding?.notice}
                    initialTemplateId={noticeTemplateIdOf(action)}
                  />
                ) : (
                  <EmployeeBulkActionModal
                    open={action}
                    action={action}
                    onClose={onClose}
                    onRemove={onRemove}
                    queryKey={QUERY_KEY.employee}
                    selectedRowKeys={[employeeId]}
                    setQueryParams={setQueryParams}
                  />
                )
              }
            </TableActionDropdown>
          )}
          <ProfileGuideButton onClick={onOpenGuide} />
        </div>
      )}
    </header>
  );
}
